// @flow
/* eslint-disable ta-code-conventions/has-matching-test,ta-code-conventions/has-matching-storydoc */

import * as React from 'react';
import { connect } from 'react-redux';
import { Query } from 'react-apollo';
import { localize } from '@ta/common.i18n';
import WindowScroll from '@ta/events.window-scroll';
import classNames from '@ta/common.classnames';
import localStorage from '@ta/common.localstorage';
import trackEventAJAX, { Track } from '@ta/common.tracking';
import { featureIsEnabled } from '@ta/common.features';
import { getState, OnboardingCompleteReloadKey } from '@ta/social.tooltip-state';
import styles from './ScrollFeedTooltip.less';
import LocationName from './LocationName.gql';

const THROTTLE_WAIT = 600;
const UNSCOPED_GEO = 1;
const module = 'social_tooltip';

type ExternalProps = {|
  elementToScrollTo: React.ElementRef<*>,
|};

type Props = {|
  locationId: number,
  loggedInUserId: string,
  hasClosedOnboarding: boolean,
  ...ExternalProps,
|};

type State = {|
  hasScrolled: boolean,
  isHalfSecondAfterMount: boolean,
|};

const SCROLL_OFFSET = 54;

/** Parses the fid out of the url for tracking. */
const getFidFromUrl = () => {
  let result = 'no-fid';
  // eslint-disable-next-line no-restricted-globals
  window.location.search
    .substr(1)
    .split('&')
    .forEach(item => {
      const tmp = item.split('=');
      if (tmp[0] === 'fid') {
        result = decodeURIComponent(tmp[1]);
      }
    });
  return result;
};

class ScrollFeedTooltip extends React.Component<Props, State> {
  state = { hasScrolled: false, isHalfSecondAfterMount: false };
  componentDidMount() {
    setTimeout(() => {
      this.setState({ isHalfSecondAfterMount: true });
    }, 500);
  }

  onScroll = () => {
    if (this.shouldShow() && !this.state.hasScrolled) {
      this.setState({ hasScrolled: true });
      const context = getFidFromUrl();
      trackEventAJAX({
        module,
        action: 'discover_your_feed_scrolled',
        context,
        value: this.props.locationId,
      });
      localStorage.remove(OnboardingCompleteReloadKey);
    }
  };

  shouldShow = () => {
    if (featureIsEnabled('social_scoped_home_scrolling_tooltip')) {
      return false;
    }

    const MAX_TA_SOCIAL_SCROLL_KEY = 'maxTASocialScroll';
    const maxScrolled = localStorage.get(MAX_TA_SOCIAL_SCROLL_KEY);
    const moreVisibleShow =
      !this.state.hasScrolled &&
      this.state.isHalfSecondAfterMount &&
      (!maxScrolled || maxScrolled < 3);
    if (featureIsEnabled('ml_more_visible_scroll_tooltip')) {
      return moreVisibleShow;
    }
    if (moreVisibleShow) {
      // If in control, track when the test would have showed
      const context = getFidFromUrl();
      trackEventAJAX({
        module,
        action: 'discover_your_feed_control_ghost_impression',
        context,
        value: this.props.locationId,
      });
    }

    return (
      (this.props.hasClosedOnboarding || this.foundOnPageReload()) &&
      this.props.locationId === UNSCOPED_GEO &&
      this.props.loggedInUserId !== null &&
      !this.state.hasScrolled &&
      this.state.isHalfSecondAfterMount
    );
  };

  foundOnPageReload = () => !!localStorage.get(OnboardingCompleteReloadKey);

  render() {
    const tooltip = (
      <div
        className={styles.toolTip}
        onClick={() => {
          const context = getFidFromUrl();
          trackEventAJAX({
            module,
            action: 'discover_your_feed_click',
            context,
            value: this.props.locationId,
          });
          // eslint-disable-next-line no-restricted-globals
          const scrollTop = window.pageYOffset;
          // eslint-disable-next-line no-restricted-globals
          window.scrollTo({
            top:
              this.props.elementToScrollTo.current.getBoundingClientRect().top +
              scrollTop -
              SCROLL_OFFSET,
            behavior: 'smooth',
          });
        }}
      >
        <Track
          visible={{
            module,
            action: 'discover_your_feed_shown',
            context: '',
            value: this.props.locationId,
          }}
        >
          <div className={styles.toolTipText}>
            {this.props.locationId === UNSCOPED_GEO ? (
              localize(
                'tooltips_scroll_travel_feed_1',
              ) /* Discover great ideas in your travel feed. */
            ) : (
              <Query query={LocationName} variables={{ locationIds: [this.props.locationId] }}>
                {({ loading, data }) => {
                  if (loading) {
                    return null;
                  }
                  return localize('tooltips_geo_updated_1', {
                    // $FlowFixGraphql
                    Geo: data.locations[0].name,
                  }); /* Discover {Geo} */
                }}
              </Query>
            )}
          </div>
          <div
            className={classNames('ui_icon', 'single-chevron-down', styles.arrow, styles.bounce)}
          />
        </Track>
      </div>
    );

    return (
      <React.Fragment>
        <WindowScroll callback={this.onScroll} throttle={THROTTLE_WAIT} />
        {this.shouldShow() && tooltip}
      </React.Fragment>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  locationId: state.page.geoId,
  loggedInUserId: state.auth.loggedInUserId,
  hasClosedOnboarding: getState(state).onboardingClosed,
}))(ScrollFeedTooltip);
